import React, { useCallback, useMemo } from 'react';
import { Grid, FormControlLabel, FormHelperText } from '@material-ui/core';
import { FormikValues, useFormikContext } from 'formik';
import { get } from 'lodash';
import CustomCheckbox from 'components/CustomCheckbox';
import CustomRadio from 'components/CustomRadio';
import { IMultiOptionFieldProps } from './interfaces';
import { useStyles } from './styles';

const MultiOptionField: React.FC<IMultiOptionFieldProps> = ({
  name,
  options,
  hasSelectAll = false,
  selectAllLabel = 'Todos',
  indeterminateIfAny = false,
  singleSelection = false,
  direction = 'column',
  xs,
  disabled = false,
  isErrored = false,
  onChange,
  highLightFirstLabel = false,
  shrunk = false,
}: IMultiOptionFieldProps) => {
  const classes = useStyles();

  const {
    values,
    setFieldValue,
    errors,
    touched,
  } = useFormikContext<FormikValues>();

  const currentValue = get(values, name);
  const isSelected = useCallback(
    (searchedValue: string) => {
      if (typeof currentValue === 'string') {
        return currentValue === searchedValue;
      } else {
        return !!currentValue.find((v: string) => v === searchedValue);
      }
    },
    [currentValue],
  );

  const _isErrored = useMemo(
    () => isErrored || (get(touched, name) && get(errors, name)),
    [isErrored, touched, errors, name],
  );

  const handleSelectionChange = useCallback(
    (searchedValue: string) => {
      onChange && onChange(searchedValue);
      if (typeof currentValue === 'string') {
        setFieldValue(name, searchedValue, true);
        return;
      } else {
        const existingValueIndex = currentValue.findIndex(
          (v: string) => v === searchedValue,
        );
        if (existingValueIndex === -1) {
          setFieldValue(name, [...currentValue, searchedValue], true);
          return;
        }
        setFieldValue(
          name,
          currentValue.filter((v: string) => v !== searchedValue),
          true,
        );
      }
    },
    [currentValue, name, setFieldValue],
  );

  const getAreAllOptionsSelected = (options) =>
    getAllChildrenValues(options).every((x) => currentValue.includes(x));

  const getIsAnyOptionSelected = (options) =>
    getAllChildrenValues(options).some((x) => currentValue.includes(x));

  const getAllChildrenValues = (options) => {
    const childrenValues = [];
    options.forEach(({ value, fieldOptions }) => {
      if (fieldOptions) {
        childrenValues.push(...getAllChildrenValues(fieldOptions));
      } else {
        childrenValues.push(value);
      }
    });
    return childrenValues;
  };

  const toggleSelectAll = (options, value) => () => {
    if (getAreAllOptionsSelected(options)) {
      setFieldValue(
        name,
        currentValue.filter((v: string) => !v.includes(value)),
        true,
      );
      return;
    }
    setFieldValue(
      name,
      Array.from(new Set([...currentValue, ...getAllChildrenValues(options)])),
      true,
    );
  };

  const ControlComponent = useMemo(
    () => (singleSelection ? CustomRadio : CustomCheckbox),
    [singleSelection],
  );

  const getSubOptions = (options, direction) => {
    const subDirection = direction === 'row' ? 'column' : 'row';
    return options.map(({ value, label, fieldOptions }) => {
      const fieldOptionsLabels =
        label === 'Oi' ||
        label === 'Claro' ||
        label === 'Vivo' ||
        label === 'Tim' ||
        label === 'Migrado' ||
        label === 'Não Migrado';
      return (
        <Grid
          key={value}
          item
          xs={xs}
          direction={subDirection}
          className={subDirection === 'row' ? classes.subOptionsInline : ''}
        >
          <FormControlLabel
            className={
              (shrunk ? classes.shrunk : '') +
              (highLightFirstLabel && options.length === 1
                ? classes.highLightLabel
                : '') +
              (label === 'TV' ? classes.styleSubSystem : '') +
              (fieldOptionsLabels ? ` ${classes.styleFieldOptionsLabels}` : '')
            }
            control={
              fieldOptions ? (
                <CustomCheckbox
                  checked={
                    indeterminateIfAny
                      ? getIsAnyOptionSelected(fieldOptions)
                      : getAreAllOptionsSelected(fieldOptions)
                  }
                  indeterminate={
                    indeterminateIfAny &&
                    getIsAnyOptionSelected(fieldOptions) &&
                    !getAreAllOptionsSelected(fieldOptions)
                  }
                  onChange={toggleSelectAll(fieldOptions, value)}
                  error={!!_isErrored}
                />
              ) : (
                <ControlComponent
                  checked={
                    isSelected(value) || getAreAllOptionsSelected(options)
                  }
                  onChange={() => handleSelectionChange(value)}
                  name={name}
                  error={!!_isErrored}
                />
              )
            }
            label={label}
            disabled={disabled}
          />
          <Grid item container direction={subDirection}>
            {fieldOptions && getSubOptions(fieldOptions, subDirection)}
          </Grid>
        </Grid>
      );
    });
  };

  return (
    <Grid
      item
      xs={12}
      container
      direction={direction}
      justifyContent="flex-start"
      alignItems="flex-start"
    >
      <Grid item xs={12}>
        {typeof _isErrored === 'string' && (
          <FormHelperText error>{_isErrored}</FormHelperText>
        )}
      </Grid>
      {hasSelectAll && (
        <Grid item xs={xs}>
          <FormControlLabel
            className={highLightFirstLabel ? classes.highLightLabel : ''}
            control={
              <CustomCheckbox
                checked={
                  indeterminateIfAny
                    ? getIsAnyOptionSelected(options)
                    : getAreAllOptionsSelected(options)
                }
                indeterminate={
                  indeterminateIfAny &&
                  getIsAnyOptionSelected(options) &&
                  !getAreAllOptionsSelected(options)
                }
                onChange={toggleSelectAll(options, '')}
                error={!!_isErrored}
              />
            }
            label={selectAllLabel}
            disabled={disabled}
          />
        </Grid>
      )}
      {options && getSubOptions(options, direction)}
    </Grid>
  );
};

export default MultiOptionField;
